import type { LoadState } from './ScreenStates';

export interface LoadableQuery {
  readonly data: unknown;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly fetchStatus: 'fetching' | 'paused' | 'idle';
  readonly error: unknown;
  readonly errorUpdateCount: number;
  readonly failureCount: number;
  readonly failureReason: unknown;
  readonly refetch: () => unknown;
}

export const loadStateOf = (query: LoadableQuery): LoadState => {
  const paused = query.fetchStatus === 'paused';
  const retryWaiting = paused && query.failureCount > 0;
  const retrying = query.data === undefined && query.isFetching && query.errorUpdateCount > 0;
  const hasData = query.data !== undefined;
  return {
    hasData,
    isError: query.isError || retryWaiting || retrying,
    isPaused: paused && query.failureCount === 0,
    isRetrying: retrying,
    isStale: hasData && (paused || query.isError),
    loadError: query.error ?? query.failureReason,
    refetch: () => {
      void query.refetch();
    },
  };
};

export const combineLoadStates = (...states: readonly LoadState[]): LoadState => {
  const blocking = states.filter((state) => !state.hasData);
  return {
    hasData: blocking.length === 0,
    isError: blocking.some((state) => state.isError),
    isPaused: blocking.some((state) => state.isPaused),
    isRetrying: blocking.some((state) => state.isRetrying),
    isStale: states.some((state) => state.isStale === true),
    loadError: blocking.find((state) => state.isError)?.loadError ?? null,
    refetch: () => states.forEach((state) => state.refetch()),
  };
};
